import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import React, { useState } from 'react';

// Check out the Bokeh storybook
// https://bokeh.vimeows.com/
import {
  Button,
  rem,
  useDisclosure,
  useBreakpointValue,
  NestedMenu,
  NestedMenuContent,
  NestedMenuItem,
  NestedMenuTrigger,
  NestedMenuPositioner,
  NestedMenuDivider,
} from '@vimeo/bokeh';
import {
  _3GridLeftLayout,
  Upload,
  Record,
  CameraOn,
  ClapperBoard,
  DownloadImport,
  PlusSmall,
} from '@vimeo/bokeh-icons';
import { useCapability } from '@vimeo/core/hooks/use-capability';
import { translate } from '@vimeo/i18n-runtime';
import { useHeaderTracking } from '@vimeo/picox';
import { getPageNameFromPath } from '@vimeo/ui/core/F2PJoinLogin/utils';
import { useViewer, Viewer } from '@vimeo/viewer';

import { useShowcaseNewDropdownMenu } from '../../hooks/use-showcase-new-dropdown-menu';
import { trackNavigationActionEvent } from '../../tracking';

const UpsellModal = dynamic(() => import('@vimeo/upsell-modal'));
const ImportModal = dynamic(() => import('@vimeo/import-modal'), { ssr: false });

const EventCreationWizardModal = dynamic(
  async () => {
    const { EventCreationWizardModal } = await import('@vimeo/event-creation-wizard');
    return { default: EventCreationWizardModal };
  },
  { ssr: false },
);

const MobileContentMenu = dynamic(
  async () => {
    const { MobileContentMenu } = await import('./MobileContentMenu');
    return { default: MobileContentMenu };
  },
  {
    ssr: false,
  },
);

/**
 * The `NewContentMenu` component provides a dropdown menu to navigate to pages where users an upload, record, host, or create videos.
 */
export function NewContentMenu({ hasUploader }: { hasUploader?: boolean }): React.ReactElement {
  const viewer = useViewer();

  if (
    !viewer ||
    viewer.teamUser?.plainTextPermissionLevel === 'Viewer' ||
    viewer.isSimplifiedSite
  ) {
    return <></>;
  } else {
    return <ContentMenu viewer={viewer} hasUploader={hasUploader} />;
  }
}

function ContentMenu({
  viewer,
  hasUploader,
}: {
  viewer: Viewer;
  hasUploader?: boolean;
}): React.ReactElement {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const teamOwnerId = viewer.teamUser?.ownerId ?? viewer.user?.id;
  const { capabilities, loading, error } = useCapability([
    'hasCreation',
    'canManageTeamCollections',
  ]);
  const hasCreation = capabilities.hasCreation && !loading && !error;
  const canManageTeamCollections = capabilities.canManageTeamCollections && !loading && !error;
  const [showEventCreationWizard, setShowEventCreationWizard] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const {
    capabilities: ownerCapabilities,
    loading: ownerCapabilitiesLoading,
    error: ownerCapabilitiesError,
  } = useCapability(['canCreateLiveEvents'], teamOwnerId);
  const canCreateLiveEvents =
    ownerCapabilities.canCreateLiveEvents && !ownerCapabilitiesLoading && !ownerCapabilitiesError;

  const match = window.location.pathname.match(/\/folders?\/(\d+)/);
  const folderId = match?.[1];
  const folderIdQueryParam = folderId ? `folder_id=${folderId}` : '';
  const uploadLink = folderId ? `/upload/videos?${folderIdQueryParam}` : '/upload/videos';
  const creationWebLink = `/create/edit?blank=true${folderId ? `&${folderIdQueryParam}` : ''}`;

  const isRootFolder = window.location.pathname === '/library';
  const isOwner = viewer?.user?.id === teamOwnerId;
  const isAdmin = viewer?.teamUser?.plainTextPermissionLevel === 'Admin';
  const shouldRecordInRoot = isRootFolder && (isOwner || isAdmin);
  const recordLink = viewer.isRecordToolSupported
    ? `/record/start-recording${
        folderId ? `?${folderIdQueryParam}` : shouldRecordInRoot ? '?folder_id=root' : ''
      }`
    : '/features/screen-recorder';
  const creationLink = isMobile ? 'https://vimeo.page.link/createvideo' : creationWebLink;

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    canCreateShowcase,
    isCreatingShowcase,
    isShowcaseUpsellModalActive,
    onCreateShowcaseClick,
    setIsShowcaseUpsellModalActive,
  } = useShowcaseNewDropdownMenu({ canManageTeamCollections, viewer });

  const { trackHeaderCreateButtonClicked, trackHeaderCreateMenuItemClicked } = useHeaderTracking();

  return (
    <>
      <NestedMenu positioning={{ placement: 'bottom-end' }}>
        <NestedMenuTrigger
          onClick={(e) => {
            if (isMobile) {
              e.preventDefault();
              onOpen();
            }
            trackNavigationActionEvent({
              copy: 'new',
              element: 'dropdown_header',
              viewer,
            });
            trackHeaderCreateButtonClicked();
          }}
          as={Button}
          variant="primary"
          size={{ base: 'sm', sm: 'md' }}
          data-id="new_content_menu_button"
          leftIcon={<PlusSmall />}
        >
          {translate('Create')}
        </NestedMenuTrigger>
        <NestedMenuPositioner>
          <NestedMenuContent minWidth={rem(202)}>
            <NestedMenuItem
              id="upload"
              value="upload"
              as={NextLink}
              /* @ts-expect-error `href` does work with `as` set to an `a` element */
              href={uploadLink}
              prefetch={false}
              onClick={() => {
                trackNavigationActionEvent({
                  copy: 'upload',
                  eventName: 'vimeo.start_new_video_flow',
                  feature: 'upload',
                  element: 'dropdown',
                  entityType: 'video',
                  targetPath: '/upload',
                  target: 'upload_page',
                  viewer,
                });
                trackHeaderCreateMenuItemClicked({ createMenuItem: 'upload' });
              }}
            >
              <Upload /> {translate('Upload')}
            </NestedMenuItem>
            {hasUploader ? (
              <NestedMenuItem
                id="import"
                value="import"
                onClick={() => {
                  setShowImportModal(true);
                  trackHeaderCreateMenuItemClicked({ createMenuItem: 'import' });
                }}
              >
                <DownloadImport /> {translate('Import')}
              </NestedMenuItem>
            ) : null}

            <NestedMenuDivider />
            {hasCreation && (
              <NestedMenuItem
                id="create"
                value="create"
                as={NextLink}
                /* @ts-expect-error `href` does work with `as` set to an `a` element */
                href={creationLink}
                prefetch={false}
                onClick={() => {
                  trackNavigationActionEvent({
                    copy: 'create_video',
                    eventName: 'vimeo.start_new_video_flow',
                    feature: 'create',
                    element: 'dropdown',
                    targetPath: '/create/edit?blank=true',
                    target: 'vimeo_create',
                    viewer,
                  });
                  trackHeaderCreateMenuItemClicked({ createMenuItem: 'video_project' });
                }}
              >
                <ClapperBoard /> {translate('Video project')}
              </NestedMenuItem>
            )}
            <NestedMenuItem
              id="record"
              value="record"
              as={NextLink}
              /* @ts-expect-error `href` does work with `as` set to an `a` element */
              href={recordLink}
              prefetch={false}
              onClick={() => {
                trackNavigationActionEvent({
                  copy: 'record_screen',
                  eventName: 'vimeo.start_new_video_flow',
                  feature: 'record',
                  element: 'dropdown',
                  targetPath: '/features/screen-recorder',
                  target: 'vimeo_record',
                  viewer,
                });
                trackHeaderCreateMenuItemClicked({ createMenuItem: 'recording' });
              }}
            >
              <Record /> {translate('Recording')}
            </NestedMenuItem>
            {canCreateLiveEvents && (
              <NestedMenuItem
                id="host"
                value="host"
                onClick={() => {
                  trackNavigationActionEvent({
                    copy: 'host_event',
                    eventName: 'vimeo.start_new_video_flow',
                    feature: 'live_event',
                    element: 'dropdown',
                    target: 'live_unified_event_entry',
                    viewer,
                  });
                  trackHeaderCreateMenuItemClicked({ createMenuItem: 'event' });
                  setShowEventCreationWizard(true);
                }}
              >
                <CameraOn /> {translate('Event')}
              </NestedMenuItem>
            )}
            {canCreateShowcase && (
              <NestedMenuItem
                id="showcase"
                value="showcase"
                disabled={isCreatingShowcase}
                onClick={() => {
                  trackHeaderCreateMenuItemClicked({ createMenuItem: 'showcase' });
                  onCreateShowcaseClick();
                }}
              >
                <_3GridLeftLayout /> {translate('Showcase')}
              </NestedMenuItem>
            )}
          </NestedMenuContent>
        </NestedMenuPositioner>
        <EventCreationWizardModal
          active={showEventCreationWizard}
          activeSet={setShowEventCreationWizard}
        />
      </NestedMenu>
      <MobileContentMenu
        isOpen={isOpen}
        onClose={onClose}
        canCreateLiveEvents
        uploadLink={uploadLink}
        recordLink={recordLink}
        createShowcaseProps={{
          canCreateShowcase,
          isCreatingShowcase,
          onCreateShowcaseClick,
        }}
        {...(hasCreation && { creationLink })}
      />
      {isShowcaseUpsellModalActive && (
        <UpsellModal
          apiUrl={viewer?.apiUrl}
          onClose={() => setIsShowcaseUpsellModalActive(false)}
          templateType="default"
          tracking={{
            params: {
              feature: 'showcases',
              location: 'top_navigation_bar',
              page: getPageNameFromPath(window.location.pathname, viewer?.user) || 'ss_lihp',
              upsell_name: 'showcase_limit',
            },
            paywallTracking: {
              paywallTrigger: 'top_navigation_new_menu_showcase_limit_button',
              paywallLocation: 'top_navigation',
              paywallType: 'popup',
              paywallFeature: 'showcases',
            },
          }}
          userConfig={{ jwt: viewer?.jwt, userId: viewer?.user?.id }}
          modalConfig={{
            mkcCode: 'unified-top-nav-legacy',
          }}
        />
      )}
      {showImportModal ? (
        <ImportModal
          isOpen={showImportModal}
          setIsOpen={setShowImportModal}
          referrerPage="create_menu"
          isAutomatic={false}
        />
      ) : null}
    </>
  );
}
